import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronRight, ArrowUpRight } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { getConfig } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/noticeData";
import { noticesByCategory, categorySlug } from "@/lib/seoNotices";

const SITE_URL = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";

export default function NoticeTypesHub() {
  const [config, setConfig] = useState({ firm_name: "Zexo Legal", price: 249 });
  const groups = noticesByCategory();

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); window.scrollTo(0, 0); }, []);

  const canonical = `${SITE_URL}/legal-notices`;
  const itemListSchema = {
    "@context": "https://schema.org", "@type": "ItemList",
    itemListElement: groups.flatMap((g) => g.items).map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.title,
      url: `${SITE_URL}/legal-notice/${it.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Helmet>
        <title>All Legal Notice Types in India | Zexo Legal</title>
        <meta name="description" content="Browse every type of legal notice you can send with Zexo Legal — cheque bounce, unpaid salary, tenant eviction, defamation, trademark and more. Advocate-verified, from ₹249." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="All Legal Notice Types in India | Zexo Legal" />
        <meta property="og:description" content="Every legal notice type — advocate-verified, drafted and dispatched from ₹249." />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <SiteHeader firmName={config.firm_name} />

      <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-6 pt-6 lg:px-10">
        <ol className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
          <li><Link to="/" className="hover:text-navy">Home</Link></li>
          <ChevronRight className="h-3.5 w-3.5" />
          <li className="text-navy">Legal Notices</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-6xl px-6 py-8 lg:px-10">
        <h1 className="font-display text-4xl font-black leading-tight text-ink sm:text-5xl">
          Every type of legal notice, drafted &amp; advocate-verified
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
          Pick the notice that fits your situation. Each one is drafted from your answers, reviewed and stamped by a
          qualified advocate, and dispatched from our office — from ₹{config.price || 249}.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-16 lg:px-10">
        <div className="space-y-10">
          {groups.map((g) => {
            const Icon = CATEGORY_ICONS[g.category] || ArrowRight;
            return (
              <div key={g.category} data-testid={`hub-category-${g.category}`}>
                <Link to={`/legal-notices/${categorySlug(g.category)}`} data-testid={`hub-category-link-${g.category}`} className="group inline-flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Icon className="h-5 w-5" /></span>
                  <h2 className="font-display text-2xl font-extrabold text-ink group-hover:text-navy">{g.categoryTitle}</h2>
                  <ChevronRight className="h-5 w-5 text-slate-400 transition-colors group-hover:text-navy" />
                </Link>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((it) => (
                    <Link key={it.slug} to={`/legal-notice/${it.slug}`} data-testid={`hub-link-${it.slug}`}
                      className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-navy hover:shadow-lg hover:shadow-navy/10">
                      <span className="font-display text-base font-bold text-ink group-hover:text-navy">{it.title}</span>
                      <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-400 transition-colors group-hover:text-navy" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-3xl border border-navy/20 bg-navy/[0.04] p-8 text-center">
          <h2 className="font-display text-2xl font-black text-ink">Not sure which notice you need?</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600">Start the guided flow — we'll ask a few simple questions and pick the right format for you.</p>
          <Link to="/create" data-testid="hub-start-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-navy/30 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
            Generate Legal Notice <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <SiteFooter firmName={config.firm_name} />
    </div>
  );
}
